import { ArrowLeft, ChevronSelectorVertical, Copy04, Copy05, Grid01, Settings04 } from '@untitled-ui/icons-react/build/cjs';
import { debounce } from 'lodash';
import mapboxgl from 'mapbox-gl';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ActivityFeedCmp from 'src/components/ActivityFeedCmp';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import EntityChatView from 'src/components/EntityChatView';
import { toEntityChatSalesRep } from 'src/components/EntityChatView/helpers';
import LaneOverviewModel from 'src/components/LaneOverviewModel';
import NotFoundUI from 'src/components/NotFoundUI';
import OutsideClickHandler from 'src/components/OutsideClickHandler';
import PageSectionLayout from 'src/components/PageSectionLayout';
import Header from 'src/components/PageSectionLayout/Header/Header';
import TabButton from 'src/components/TabButton';
import TooltipCmp from 'src/components/TooltipCmp';
import WidgetSettingsModal from 'src/components/WidgetSettingsModal';
import { IWidget } from 'src/components/WidgetSettingsModal/widgetSettings.interface';
import { getBadgeLabelByQuoteStatus, getBadgeTypeByQuoteStatus, STATUS } from 'src/constants/common';
import { PATH } from 'src/constants/path';
import { ROUTES } from 'src/constants/routes';
import { setCurrentUser } from 'src/redux/auth.slice';
import { appearance as appearanceSelector } from 'src/redux/basic.slice';
import { resetForm, setCurrentStep, setQuoteBookingFormData } from 'src/redux/bookingStepper.slice';
import { useAppSelector } from 'src/redux/hooks';
import {
    activitiesPaginationData,
    appendActivities,
    setAddressDetails,
    setActivities,
    setActivitiesLoading,
    setActivitiesPagination,
    setDimensions,
    setServices,
    isShowQuoteDetailModalData,
    isQuoteDetailsUpdatedData,
    setIsQuoteDetailsUpdated,
    setActiveQuotes as setActiveQuotesRedux,
    setActiveQuoteColumns,
    setActiveQuotesFetchedForId,
    resetAllQuoteState,
    activeQuotesData as activeQuotesSelector,
    updateActiveQuoteValue,
} from 'src/redux/Quoting.slice';
import { RootState } from 'src/redux/store';
import { updateUserWidgets } from 'src/services/AuthService';
import { createQuoteCustomerRequest, getActiveQuotes, getQuote, getQuoteActivities } from 'src/services/QuoteService';
import { getLabelByValue } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';
import { transformWidgetsToApiFormat, WIDGET_SECTION_TYPE } from 'src/utils/widgetSettings.utils';

import useDuplicateQuote from '../AddQuote/useDuplicateQuote';
import { SelectQuoteBlock, TermsAndConditions } from '../Component';
import { IQuotePreparingLaneContext } from '../Component/QuotePreparingLoader/laneInsights';
import BookOrderBillingAccountModal from '../Modals/BookOrderBillingAccountModal';
import DatePickupModel from '../Modals/DatePickupModel';
import QuoteDetailModalNew from '../Modals/QuoteDetailModalNew';
import StopDetailsModal from '../Modals/StopDetailsModal';

import CustomerPortalStopMetaStrip, { buildStopCards } from './CustomerPortalStopMetaStrip';
import { getRouteStats } from './MapBox/MapBoxMap';
import MultiStopMapItWidget from './MapIt/MultiStopMapItWidget';
import MessagesWidget from './MessagesWidget';
import QuoteDetailMetaDataTable from './QuoteDetailMetaDataTable';
import { getQuoteDimensions } from './quoteDetails.helper';
import { DEFAULT_QUOTE_WIDGET_SETTINGS, QUOTE_WIDGET_IDS, transformQuoteWidgets, Widget } from './quoteWidgetSettings.helper';

const QuoteDetail = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const { id } = useParams();
    const dispatch = useDispatch();
    const appearance = useSelector(appearanceSelector);
    const activeQuotes = useAppSelector(activeQuotesSelector);
    // Hoisted so both the Select-a-Quote table below and the header's new Order ID button
    // (client decision — moved out of that table's trailing column) share one definition.
    const isToBookStatus = useMemo(() => activeQuotes.some((q: any) => q.status === STATUS.TO_BOOK || q.status === STATUS.WON), [activeQuotes]);

    const { currentUser } = useSelector((state: RootState) => state?.auth);
    const { duplicateQuote, duplicatingQuoteId } = useDuplicateQuote();
    const { socketIo, isSocketConnected, handlingUnitOptions, services } = useSelector((state: RootState) => state.basic);
    const unreadMessageCount = useSelector((state: RootState) => (state as any).quoteChat?.unreadCount ?? 0);
    const pagination = useSelector(activitiesPaginationData);
    const isShowQuoteDetailModal = useSelector(isShowQuoteDetailModalData);
    const isQuoteDetailsUpdated = useSelector(isQuoteDetailsUpdatedData);
    const quoteId = location?.state?.id;
    // The ONE identifier every child of this page should key off. `location.state` is deliberately
    // nulled right after mount (see the effect that calls navigate(..., { state: null })), so any
    // value derived straight from `quoteId || id` silently changes shape mid-session: the numeric
    // `location.state.id` on the first render, then the string `useParams().id` once state is
    // cleared. Anything using it as an effect dependency re-ran on that flip — which is what made
    // the Messages tab re-fetch its thread and members a second time on every quote open.
    // String() both branches so the value is identical before and after the state clear.
    const currentQuoteId = quoteId != null ? String(quoteId) : id;
    // Only true when redirected here straight from creating a quote; drives
    // showing "Preparing your quote" immediately instead of the generic table
    // skeleton for that entry path specifically. Re-derived below whenever
    // `id` changes (NOT captured once at mount): react-router reuses this
    // same component instance across :id changes on this route — it does not
    // remount just because the user landed on a different quote — so a
    // mount-only capture would freeze at whatever this instance's very first
    // quote page load saw, ignoring every quote viewed afterward.
    const [isNewQuote, setIsNewQuote] = useState(() => Boolean(location?.state?.isNewQuote));

    useEffect(() => {
        setIsNewQuote(Boolean(location?.state?.isNewQuote));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);
    const [quotingDetails, setQuotingDetails] = useState<any>({});
    const [code, setCode] = useState('');
    // Starts true exactly when the fetch-trigger effect below will call
    // getQuoteDetails (any route :id to fetch) — avoids a one-frame flash where
    // isLoading reads false before that effect runs.
    const [isLoading, setIsLoading] = useState(() => Boolean(id));
    const [, setQuoteConnectedUsers] = useState<{ src: string; alt: string }[]>([]);

    // Value itself was only ever read by the removed "Quote Submitted" fallback
    // screen; the setter is still wired up for DatePickupModel / QuoteDetailModalNew.
    const [, setIsChangedByCustomer] = useState<boolean>(false);
    const [isLoadingActiveQuotes, setIsLoadingActiveQuotes] = useState(true);
    const [isShowLaneOverviewModal, setIsShowLaneOverviewModal] = useState(false);
    // Holds the addresses resolved for the route that was on screen when Open was clicked (see
    // getStopAddressByPosition/getStopsAddressesBetween below) — waypoints carries every stop
    // strictly in between so Lane Overview's map mirrors the Map-It widget's single joined route.
    const [laneOverviewRoute, setLaneOverviewRoute] = useState<{
        from: { fullAddress?: string; latitude?: string | number | null; longitude?: string | number | null; country?: string };
        to: { fullAddress?: string; latitude?: string | number | null; longitude?: string | number | null; country?: string };
        waypoints: { fullAddress?: string; latitude?: string | number | null; longitude?: string | number | null; country?: string }[];
    } | null>(null);
    const [isShowDatePickupModal, setIsShowDatePickupModal] = useState(false);
    // Read-only Stops Summary info modal, opened ONLY by the metadata card's "Quote Type" cell.
    // Root-level (not tab-gated) because that card sits above the tabs and is visible on every tab
    // — same pattern as this page's other root-level modals (isShowLaneOverviewModal,
    // isShowDatePickupModal, isWidgetsModalOpen).
    const [isShowStopDetailsModal, setIsShowStopDetailsModal] = useState(false);
    // The carrier row the customer pressed Book Order on, parked while they pick a billing account
    // (see handleBookOrder). Non-null is what holds the modal open.
    const [pendingBookingQuoteData, setPendingBookingQuoteData] = useState<any>(null);
    const [activeTab, setActiveTab] = useState(() => (location?.state?.openMessagesTab ? 'messages' : 'overview'));
    const [isWidgetsModalOpen, setIsWidgetsModalOpen] = useState(false);
    const [isShowQuoteActionsDropDown, setIsShowQuoteActionsDropDown] = useState(false);

    const widgets = useMemo<Widget[]>(() => {
        const settings = currentUser?.quoteWidgetSettings ?? DEFAULT_QUOTE_WIDGET_SETTINGS;

        if (currentUser?.quoteWidgetSettings && !(QUOTE_WIDGET_IDS.MESSAGES in settings)) {
            const maxPosition = Object.values(settings as Record<string, { isEnabled: boolean; position: number }>).reduce((max, w) => Math.max(max, w.position), -1);

            const settingsWithMessages = {
                ...settings,
                [QUOTE_WIDGET_IDS.MESSAGES]: { isEnabled: true, position: maxPosition + 1 },
            };

            return transformQuoteWidgets(settingsWithMessages);
        }

        return transformQuoteWidgets(settings);
    }, [currentUser?.quoteWidgetSettings]);

    const isWidgetsDisabled = useMemo(() => {
        if (!widgets.length) return false;

        return widgets.every((widget) => !widget.isEnabled);
    }, [widgets]);

    const quoteDetailTabs = useMemo(
        () => [
            { value: 'overview', name: 'Overview' },
            // Chat is unavailable for WAL staff sessions (no contact participant row) — the same
            // gate the Messages widget uses. The backend 403s every chat endpoint for them, so the
            // tab could only ever show a broken, empty thread.
            ...(currentUser?.isWalUserSession
                ? []
                : [
                      {
                          value: 'messages',
                          name: 'Messages',
                          count: unreadMessageCount || 0,
                          showDotAboveCount: unreadMessageCount > 0,
                      },
                  ]),
        ],
        [unreadMessageCount, currentUser?.isWalUserSession]
    );
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const rightScrollContainerRef = useRef<HTMLDivElement>(null);
    const widgetsContentRef = useRef<HTMLDivElement>(null);

    const scrollWidgetsToTop = useCallback(() => {
        if (widgetsContentRef.current) {
            widgetsContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, []);

    const getQuoteDetails = async (quotingId: number) => {
        setQuotingDetails({}); // Clear data first to show loader
        setIsLoading(true);

        try {
            const response = await getQuote(quotingId);

            if (response?.data && response?.status) {
                const data = response?.data;
                setQuotingDetails(data);

                if (data?.code) {
                    setCode(data.code);
                }

                if (data?.userData) {
                    const user = data.userData;
                    let imageSrc = '';

                    if (user?.image && typeof user.image === 'string' && user.image.trim()) {
                        let tempImageSrc = '';

                        if (user.image.includes('http')) {
                            tempImageSrc = user.image;
                        } else if (user?.imageUrl && typeof user.imageUrl === 'string' && user.imageUrl.trim()) {
                            tempImageSrc = user.imageUrl + user.image;
                        } else {
                            tempImageSrc = user.image;
                        }

                        if (!tempImageSrc.includes('/null')) {
                            imageSrc = tempImageSrc;
                        }
                    }

                    const firstName = user?.firstName || '';
                    const lastName = user?.lastName || '';
                    const altText = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'User';

                    setQuoteConnectedUsers([{ src: imageSrc, alt: altText }]);
                }
            } else {
                WalToast.error(t('QuotingHub.QuotingDashboard.Quote_not_found'));
                navigate(PATH.MY_QUOTE, { replace: true });
            }
        } catch (error) {
            WalToast.error(t('QuotingHub.QuotingDashboard.Quote_not_found'));
            navigate(PATH.MY_QUOTE, { replace: true });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchActiveQuotes = async (quoteIdParam: string | number) => {
        if (!quoteIdParam) {
            return;
        }

        try {
            setIsLoadingActiveQuotes(true);
            const response = await getActiveQuotes(Number(quoteIdParam));

            if (response?.data && !response?.data?.isChangedByCustomer) {
                const quotesData = Array.isArray(response.data?.activeQuote) ? response.data?.activeQuote : [];
                dispatch(setActiveQuotesRedux(quotesData));
                dispatch(setActiveQuoteColumns(response.data?.quoteColumns ?? null));
                dispatch(setActiveQuotesFetchedForId(quoteIdParam));
            } else {
                setIsChangedByCustomer(response?.data?.isChangedByCustomer);
            }
        } catch (error: any) {
            console.error('Error fetching active quotes:', error);
            dispatch(setActiveQuotesRedux([]));
        } finally {
            setIsLoadingActiveQuotes(false);
        }
    };

    // Bug fix (user-reported) — an address edit that invalidates old rates reverts the quote's
    // status server-side (e.g. Quoted -> Requested) as part of the same async rate-refresh this
    // socket listener is already waiting on. That refresh only re-fetched the active-quotes list,
    // never quotingDetails, so the header badge kept showing the pre-edit status until a hard
    // reload. Re-fetching just the status field here (not the full getQuoteDetails() — that clears
    // quotingDetails to {} first and would flash the whole page on every socket event) keeps the
    // badge in sync without disrupting normal rate-arrival updates.
    const refreshQuoteStatus = async (quotingId: number) => {
        try {
            const response = await getQuote(quotingId);

            if (response?.data?.status) {
                setQuotingDetails((prev: any) => ({ ...prev, status: response.data.status }));
            }
        } catch (error) {
            // Non-fatal — the rates list itself still refreshes via fetchActiveQuotes below;
            // a stale header status will still resolve on the next full page load.
        }
    };

    // Socket: join quote room and listen for events (same pattern as ChatView)
    useEffect(() => {
        if (!socketIo || !isSocketConnected || !currentQuoteId) return;

        const roomName = `quote_fetched_${currentQuoteId}`;

        socketIo.request({ method: 'POST', url: '/subscribe/joinRoom', data: { roomNames: [roomName] } }, () => {});

        const handleQuoteOptionReady = () => {
            fetchActiveQuotes(currentQuoteId);
            refreshQuoteStatus(Number(currentQuoteId));
        };

        const handleLoadingStop = () => {
            fetchActiveQuotes(currentQuoteId);
            refreshQuoteStatus(Number(currentQuoteId));
        };

        socketIo.on('quote_option_ready', handleQuoteOptionReady);
        socketIo.on('quote_loading_stop', handleLoadingStop);

        return () => {
            socketIo.off('quote_option_ready', handleQuoteOptionReady);
            socketIo.off('quote_loading_stop', handleLoadingStop);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socketIo, isSocketConnected, currentQuoteId]);

    // Fallback: if loading stays true for 2 min (no socket event), fetch manually
    useEffect(() => {
        if (!isLoadingActiveQuotes) return;

        const fallbackTimer = setTimeout(() => {
            if (currentQuoteId) {
                fetchActiveQuotes(currentQuoteId);
            }
        }, 60 * 1000);

        return () => clearTimeout(fallbackTimer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingActiveQuotes]);

    const copyDebounce = useCallback(
        debounce((copyQuote: string) => {
            navigator.clipboard.writeText(copyQuote);
            WalToast.copy(t('Common.Copied_to_clipboard'), '', 'copy-msg');
        }, 0),

        []
    );

    useEffect(
        () => () => {
            dispatch(resetAllQuoteState());
            dispatch(setActivities([]));
            dispatch(setActivitiesPagination(null));
        },
        []
    );

    const fetchActivities = useCallback(
        async ({ lastId, append }: { lastId?: number | null; append: boolean }) => {
            const entityId = Number(currentQuoteId);
            if (!entityId) return;

            dispatch(setActivitiesLoading(true));

            try {
                const response = await getQuoteActivities({ entityType: 'quote', entityId, perPage: 25, ...(lastId ? { lastId } : {}) });
                const data = response?.data;

                if (append) {
                    dispatch(appendActivities(data?.activities || []));
                } else {
                    dispatch(setActivities(data?.activities || []));
                }

                dispatch(
                    setActivitiesPagination(
                        data?.pagination
                            ? {
                                  hasMore: data.pagination.hasMore ?? false,
                                  nextCursor: data.pagination.nextCursor ?? null,
                                  totalItems: data.pagination.totalItems ?? 0,
                              }
                            : null
                    )
                );
            } catch (err: any) {
                WalToast.error(err?.response?.data?.message || 'Failed to load activities');
            } finally {
                dispatch(setActivitiesLoading(false));
            }
        },
        [currentQuoteId, dispatch]
    );

    useEffect(() => {
        if (!id) return;

        fetchActivities({ append: false });
    }, [id]);

    const handleLoadMore = useCallback(async () => {
        if (!pagination?.hasMore || !pagination?.nextCursor) return;

        fetchActivities({ lastId: pagination.nextCursor, append: true });
    }, [pagination, fetchActivities]);

    // Task 41 (user-reported bug fix) — this used to skip the full detail fetch whenever
    // `location.state.id` was present (i.e. whenever the user arrived via client-side nav from
    // the listing page, which hands over a PARTIAL row object as router state for a fast initial
    // paint). That partial object has no `stops`/`multiStopSummary`/dimension pickupStopId-
    // dropStopId — so Stops Summary and the Edit modal's per-stop tabs silently never rendered
    // unless the user did a hard refresh (which always has `location.state === undefined`, so the
    // guard never applied there). Always fetch full details for `id` — the partial state (set by
    // the effect below) still paints instantly; this fetch's response then overwrites it with the
    // authoritative full record, same as a hard refresh does.
    useEffect(() => {
        if (id) {
            getQuoteDetails(Number(id));
        }
    }, [id]);

    useEffect(() => {
        if (!currentQuoteId) return;

        if (location?.state?.isNewQuote) {
            dispatch(setActiveQuotesRedux([]));
            setIsLoadingActiveQuotes(true);
        } else {
            fetchActiveQuotes(currentQuoteId);
        }
    }, [currentQuoteId]);

    useEffect(() => {
        if (isQuoteDetailsUpdated) {
            if (currentQuoteId) {
                getQuoteDetails(Number(currentQuoteId));
                setIsLoadingActiveQuotes(true);
            }
            dispatch(setIsQuoteDetailsUpdated(false));
        }
    }, [isQuoteDetailsUpdated]);

    useEffect(() => {
        // Open the Messages tab when navigating from a message notification
        if (location?.state?.openMessagesTab) {
            setActiveTab('messages');
        }

        if (location?.state?.id) {
            setQuotingDetails(location?.state);
        }

        if (location?.state?.code) {
            setCode(location?.state?.code);
        }

        if (location?.state?.userData) {
            const user = location?.state?.userData;

            let imageSrc = '';

            if (user?.image && typeof user.image === 'string' && user.image.trim()) {
                let tempImageSrc = '';

                if (user.image.includes('http')) {
                    tempImageSrc = user.image;
                } else if (user?.imageUrl && typeof user.imageUrl === 'string' && user.imageUrl.trim()) {
                    tempImageSrc = user.imageUrl + user.image;
                } else {
                    tempImageSrc = user.image;
                }

                if (!tempImageSrc.includes('/null')) {
                    imageSrc = tempImageSrc;
                }
            }

            const firstName = user?.firstName || '';
            const lastName = user?.lastName || '';
            const altText = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'User';

            setQuoteConnectedUsers([
                {
                    src: imageSrc,
                    alt: altText,
                },
            ]);
        }

        if (location?.state) {
            const quote = location?.state;
            delete quote?.sourcePage;

            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location?.state]);

    const handlePickupDateWarningClick = () => {
        if (quotingDetails) {
            const { dimensions, selectedServices, ...rest } = quotingDetails;
            dispatch(setAddressDetails({ ...rest }));
            dispatch(setDimensions(dimensions || []));
            dispatch(setServices(selectedServices || []));
        }
        setIsShowDatePickupModal(true);
    };

    const handleDatePickupModalClose = () => {
        setIsShowDatePickupModal(false);
    };

    // Handle pickup date update from DatePickupModel - updates local state directly without API call
    const handlePickupDateUpdate = (pickupDate: string | null, deliveryDate: string | null, pickupEndDate: string | null) => {
        setQuotingDetails((prev: any) => ({
            ...prev,
            pickupDate,
            deliveryDate,
            pickupEndDate,
        }));
    };

    // Scroll to top when component mounts, quote ID changes, or content finishes loading
    useEffect(() => {
        const scrollToTop = () => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = 0;
            }

            if (rightScrollContainerRef.current) {
                rightScrollContainerRef.current.scrollTop = 0;
            }
        };

        // Scroll immediately
        scrollToTop();

        // Also scroll after a small delay to ensure content has rendered
        const timeoutId = setTimeout(scrollToTop, 100);

        return () => clearTimeout(timeoutId);
    }, [currentQuoteId, isLoading, isLoadingActiveQuotes]);

    const buildServicesFromQuote = (selectedServices: any[] = []) => {
        const serviceIds: number[] = [];
        const result: any = {};

        for (const val of selectedServices) {
            if (val.serviceId) serviceIds.push(val.serviceId);

            const slug = val?.service?.slug;

            if (slug === 'limited_access_delivery') {
                result.limitedAccess = true;
                result.limitedAccessType = val.limitedAccessType || '';
                result.limitedAccessOther = val.limitedAccessOther || '';
            } else if (slug === 'in-bond') {
                result.inbond = true;
                result.inbondType = val.inbondType || '';
                result.inbondName = val.inbondName || '';
                result.inbondAddress = val.inbondAddress || '';
                result.inbondContactMethod = val.inbondContactMethod || '';
                result.inbondContactValue = val.inbondContactValue || '';
            } else if (slug === 'dangerous_goods') {
                result.dangerousGoods = true;
                result.dangerGoodsType = val.dangerGoodsType || '';
                result.unitedNationsNumber = val.unitedNationsNumber || '';
                result.packagingGroup = val.packagingGroup || '';
                result.goodsClass = val.goodsClass || '';
                result.description = val.description || '';
                result.emergencyContactName = val.emergencyContactName || '';
                result.emergencyContactNumber = val.emergencyContactNumber || '';
                result.emergencyContactExt = val.emergencyContactExt || '';
                result.emergencyEapnbr = val.emergencyEapnbr || '';
            }
        }

        return { ...result, serviceId: serviceIds.join(',') };
    };

    /**
     * Divisions this quote can be booked under: the customer's divisions that actually have a
     * billing account. A division with none cannot produce an invoiceable order, so offering it
     * would only lead to a dead end one step later.
     */
    const bookableDivisions = useMemo(() => (currentUser?.divisions ?? []).filter((division: any) => (division?.customerBillingAccount ?? []).length > 0), [currentUser?.divisions]);

    /**
     * The division and billing account the order will be booked under, and which of the two the
     * customer still has to answer.
     *
     * A quote can reach this page missing either one. Each is resolved the same way: take what the
     * quote already fixes, otherwise take the only candidate, otherwise ask. Both are resolved
     * together because they are not independent — an account belongs to a division, and a pair that
     * disagrees would put a division on the order that its billing account does not sit under.
     *
     * When the quote fixes only the account, the division is read back off that account rather than
     * asked for, which is what stopped the review screen showing a blank division.
     */
    const bookingBillingSelection = useMemo(() => {
        const divisions = currentUser?.divisions ?? [];
        const quoteDivisionId = quotingDetails?.divisionId ?? null;
        const quoteAccountId = quotingDetails?.customerBillingAccountId ?? null;

        const allAccounts = divisions.flatMap((division: any) => (division?.customerBillingAccount ?? []).map((account: any) => ({ account, division })));

        // The account the quote already names tells us its division, even when the quote has none.
        const quoteAccountPair = quoteAccountId ? allAccounts.find((pair: any) => String(pair.account.id) === String(quoteAccountId)) : null;

        let division = divisions.find((item: any) => String(item.id) === String(quoteDivisionId)) ?? quoteAccountPair?.division ?? null;

        // A division with no billing account is not bookable, so treat it as unresolved and let the
        // customer pick one that is.
        if (division && (division.customerBillingAccount ?? []).length === 0) {
            division = null;
        }

        const needsDivision = !division && bookableDivisions.length !== 1;

        if (!division && bookableDivisions.length === 1) {
            division = bookableDivisions[0];
        }

        const divisionAccounts = division?.customerBillingAccount ?? [];
        let billingAccount = quoteAccountPair && (!division || String(quoteAccountPair.division.id) === String(division.id)) ? quoteAccountPair.account : null;

        if (!billingAccount && divisionAccounts.length === 1) {
            billingAccount = divisionAccounts[0];
        }

        const needsBillingAccount = !billingAccount;

        return {
            division,
            billingAccount,
            needsDivision,
            needsBillingAccount,
            // Nothing to book against anywhere — the one case the customer cannot resolve here.
            hasNoOptions: bookableDivisions.length === 0,
        };
    }, [currentUser?.divisions, bookableDivisions, quotingDetails?.divisionId, quotingDetails?.customerBillingAccountId]);

    /**
     * Continue into the booking stepper with the billing account this order will be billed to.
     * Split out of handleBookOrder so the flow can pause on the account modal and resume here.
     */
    const proceedToBooking = (quoteData: any, billingAccount: any, division: any) => {
        const today = moment().tz(appearance.timezone).startOf('day');
        const pickupDate = quotingDetails?.pickupDate ? moment(quotingDetails.pickupDate).startOf('day') : null;
        const deliveryDate = quotingDetails?.deliveryDate ? moment(quotingDetails.deliveryDate).startOf('day') : null;

        // Business-day-aware delivery date — anchor on the carrier's projectedDeliveryDate (the
        // same date the row's "Est. X days" count is computed against). If that date happens to
        // fall on a weekend, push it forward to the next business day so the booking never lands on Sat/Sun.
        const computeBusinessDayDeliveryDate = (): string | null => {
            if (!quoteData?.projectedDeliveryDate) return null;

            const projectedDelivery = moment.utc(quoteData.projectedDeliveryDate).startOf('day');
            const dayOfWeek = projectedDelivery.day(); // 0 = Sun, 6 = Sat

            if (dayOfWeek === 6) projectedDelivery.add(2, 'days'); // Sat → Mon
            else if (dayOfWeek === 0) projectedDelivery.add(1, 'days'); // Sun → Mon

            return projectedDelivery.format('YYYY-MM-DD');
        };

        if (pickupDate && pickupDate.isBefore(today, 'day')) {
            WalToast.error(t('QuotingHub.QuotingDashboard.Pickup_date_cannot_be_in_the_past'));

            return;
        }

        if (deliveryDate && deliveryDate.isBefore(today, 'day')) {
            WalToast.error(t('QuotingHub.QuotingDashboard.Delivery_date_cannot_be_in_the_past'));

            return;
        }

        // Both are resolved by the caller (bookingBillingSelection), which falls back to whatever the
        // quote already carries — so these are never blank on a bookable quote.
        const selectedBillingAccount = billingAccount ?? null;
        const selectedDivision = division ?? null;
        const divisionName = selectedDivision?.displayName ?? currentUser?.divisions?.find((item: any) => item.id === quotingDetails?.divisionId)?.displayName;

        const isOpenSensitivity = quotingDetails?.deliverySensitivity?.toLowerCase() === 'open';
        const isCommonServiceLevel = quoteData?.shippingServiceLevel?.toLowerCase() === 'common';

        // common + open  → business-day-aware projected date (ignores deliveryDate)
        // not common + open → null (ignores deliveryDate)
        // not open → use deliveryDate as-is
        const resolvedDeliveryDate = isOpenSensitivity ? (isCommonServiceLevel ? computeBusinessDayDeliveryDate() : null) : quotingDetails?.deliveryDate || null;

        const bookingDetails = {
            quoteId: quotingDetails?.id,
            quoteCarrierId: quoteData?.id,
            projectedDeliveryDate: quotingDetails?.deliveryDate,
            shipperDetails: {
                pickupDate: quotingDetails?.pickupDate,
                pickupEndDate: quotingDetails?.pickupEndDate ?? null,
                pickupSensitivity: quotingDetails?.pickupSensitivity ?? null,
                pickupTimeType: quotingDetails?.pickupWindowType ?? null,
                pickupStartTime: quotingDetails?.pickupStartTime ?? null,
                pickupEndTime: quotingDetails?.pickupEndTime ?? null,
                shipperId: quotingDetails?.shipperId,
                shipperCompanyName: quotingDetails?.shipperCompanyName,
                shipperFullAddress: quotingDetails?.shipperFullAddress,
                shipperAddress1: quotingDetails?.shipperAddress1,
                shipperStreetNumber: quotingDetails?.shipperStreetNumber,
                shipperCity: quotingDetails?.shipperCity,
                shipperState: quotingDetails?.shipperState,
                shipperStateCode: quotingDetails?.shipperStateCode,
                shipperCountry: quotingDetails?.shipperCountry,
                shipperPostal: quotingDetails?.shipperPostal,
                shipperLatitude: quotingDetails?.shipperLatitude,
                shipperLongitude: quotingDetails?.shipperLongitude,
                shipperCountryCode: quotingDetails?.shipperCountryCode,
            },
            consigneeDetails: {
                deliveryDate: resolvedDeliveryDate,
                deliveryEndDate: quotingDetails?.deliveryEndDate ?? null,
                deliverySensitivity: quotingDetails?.deliverySensitivity ?? null,
                deliveryTimeType: quotingDetails?.deliveryWindowType ?? null,
                deliveryStartTime: quotingDetails?.deliveryStartTime ?? null,
                deliveryEndTime: quotingDetails?.deliveryEndTime ?? null,
                consigneeId: quotingDetails?.consigneeId,
                consigneeCompanyName: quotingDetails?.consigneeCompanyName,
                consigneeFullAddress: quotingDetails?.consigneeFullAddress,
                consigneeAddress1: quotingDetails?.consigneeAddress1,
                consigneeStreetNumber: quotingDetails?.consigneeStreetNumber,
                consigneeCity: quotingDetails?.consigneeCity,
                consigneeState: quotingDetails?.consigneeState,
                consigneeStateCode: quotingDetails?.consigneeStateCode,
                consigneeCountry: quotingDetails?.consigneeCountry,
                consigneePostal: quotingDetails?.consigneePostal,
                consigneeLatitude: quotingDetails?.consigneeLatitude,
                consigneeLongitude: quotingDetails?.consigneeLongitude,
                consigneeCountryCode: quotingDetails?.consigneeCountryCode,
                consigneeContactId: quotingDetails?.consigneeContactId,
            },
            loadDetails: getQuoteDimensions(quotingDetails),
            orderDetails: {
                orderType: quotingDetails?.classification,
                equipmentType: quotingDetails?.primaryEquipmentType,
                equipmentTypeFullName: quotingDetails?.primaryEquipmentTypeFullName,
                divisionId: selectedDivision?.id ?? quotingDetails?.divisionId,
                customerBillingAccountId: selectedBillingAccount?.id ?? quotingDetails?.customerBillingAccountId,
                billingAccount: selectedBillingAccount,
                divisionName: divisionName,
                poNumber: quotingDetails?.poNumber ?? null,
                refNumber: quotingDetails?.refNumber ?? null,
                pickupNumber: quotingDetails?.pickupNumber ?? null,
                deliveryNumber: quotingDetails?.deliveryNumber ?? null,
                isLoadInsuranceEnabled: quotingDetails?.isLoadInsuranceEnabled ?? false,
                insuranceCommodity: quotingDetails?.insuranceCommodity ?? null,
                insuranceCommercialValue: quotingDetails?.insuranceCommercialValue ?? null,
                insuranceCurrencyType: quotingDetails?.insuranceCurrencyType ?? 'CAD',
            },
            selectedQuoteOption: activeQuotes.find((quote: any) => quote.id === quoteData?.id),
        };

        // Client decision — the Book Quote stepper shows the quote's existing middle stops as
        // read-only "Stop N" steps. quotingDetails.stops is already filtered server-side to
        // customer-requested stops only (see wal-customer-portal-be's filterCustomerVisibleStops),
        // and always includes the shipper/consignee bookends — only the middle ones are relevant
        // here, since Shipper/Consignee already have their own dedicated stepper steps.
        const quoteStops: any[] = quotingDetails?.stops || [];
        const maxStopPosition = quoteStops.length > 0 ? Math.max(...quoteStops.map((stop: any) => stop.position)) : null;
        const middleStops = quoteStops.filter((stop: any) => stop.position !== 1 && stop.position !== maxStopPosition);

        const bookingDetailsWithOriginal = {
            ...bookingDetails,
            carrierSlug: quoteData?.carrierSlug ?? null,
            services: buildServicesFromQuote(quotingDetails?.selectedServices || []),
            // Display-only — never read by ReviewFinalize's booking payload, so this can't reach
            // the API regardless of what's stored here.
            stops: middleStops,
            // Client decision — Review & Confirm's per-leg Route cards show board/drop unit counts.
            // Only present on genuine multi-stop quotes (getQuoteById.controller.ts gates the whole
            // block on quote_stops existing) — absent on classic quotes, where ReviewFinalize
            // synthesizes the trivial "everything boards at Shipper, drops at Consignee" case itself.
            multiStopSummary: quotingDetails?.multiStopSummary || null,
            originalAddresses: {
                shipper: {
                    city: quotingDetails?.shipperCity,
                    state: quotingDetails?.shipperState,
                    country: quotingDetails?.shipperCountry,
                },
                consignee: {
                    city: quotingDetails?.consigneeCity,
                    state: quotingDetails?.consigneeState,
                    country: quotingDetails?.consigneeCountry,
                },
            },
        };

        dispatch(resetForm());
        dispatch(setQuoteBookingFormData(bookingDetailsWithOriginal));
        dispatch(setCurrentStep(1));
        navigate(ROUTES.BOOKING_STEPPER, { replace: true });
    };

    /**
     * Book Order entry point. A quote can reach this page without a billing account, and the order
     * cannot be invoiced without one, so it is resolved here before the stepper opens: the only
     * account in the quote's division is applied silently, several means asking, none is a dead end
     * the customer cannot fix themselves. The chosen account rides along in the booking payload and
     * is written back onto the quote by the booking call.
     */
    const handleBookOrder = async (quoteData: any) => {
        const { division, billingAccount, needsDivision, needsBillingAccount, hasNoOptions } = bookingBillingSelection;

        if (hasNoOptions) {
            WalToast.error(t('MyQuote.Book_Order_No_Billing_Account'));

            return;
        }

        if (needsDivision || needsBillingAccount) {
            // Hold the carrier row the customer picked so the flow can resume once they answer.
            setPendingBookingQuoteData(quoteData);

            return;
        }

        proceedToBooking(quoteData, billingAccount, division);
    };

    const handleDuplicate = async (quoteCarrierData: any) => {
        const resolvedQuoteId = Number(currentQuoteId);
        if (!resolvedQuoteId) return;

        const carrierName = quoteCarrierData?.carrierName ?? quoteCarrierData?.name ?? '';
        const quoteCarrierId = quoteCarrierData?.id ? Number(quoteCarrierData.id) : undefined;

        try {
            await createQuoteCustomerRequest(resolvedQuoteId, {
                requestType: 'duplicate',
                quoteCarrierId,
                description: carrierName ? `${carrierName}` : undefined,
            });

            // Optimistically mark this carrier row as having a pending duplicate request.
            // Use == (not ===) because id may be stored as string in Redux after JSON parse.
            if (quoteCarrierId) {
                // eslint-disable-next-line eqeqeq
                const current = activeQuotes.find((q: any) => q.id == quoteCarrierId);
                dispatch(
                    updateActiveQuoteValue({
                        id: current?.id ?? quoteCarrierId,
                        pendingRequestTypes: [...(current?.pendingRequestTypes ?? []), 'duplicate'],
                    })
                );
            }
            WalToast.success(t('MyQuote.Duplicate_Request_Submitted'));
        } catch (error: any) {
            WalToast.error(error?.response?.data?.message || t('MyQuote.Duplicate_Request_Failed'));
        }
    };

    const handleRequestRate = async (quoteCarrierData: any) => {
        const resolvedQuoteId = Number(currentQuoteId);
        if (!resolvedQuoteId) return;

        const carrierName = quoteCarrierData?.carrierName ?? quoteCarrierData?.name ?? '';
        const quoteCarrierId = quoteCarrierData?.id ? Number(quoteCarrierData.id) : undefined;

        try {
            await createQuoteCustomerRequest(resolvedQuoteId, {
                requestType: 're_quote',
                quoteCarrierId,
                description: carrierName ? `${carrierName}` : undefined,
            });

            // Optimistically mark this carrier row as having a pending re_quote request.
            // Use == (not ===) because id may be stored as string in Redux after JSON parse.
            if (quoteCarrierId) {
                // eslint-disable-next-line eqeqeq
                const current = activeQuotes.find((q: any) => q.id == quoteCarrierId);
                dispatch(
                    updateActiveQuoteValue({
                        id: current?.id ?? quoteCarrierId,
                        pendingRequestTypes: [...(current?.pendingRequestTypes ?? []), 're_quote'],
                    })
                );
            }
            WalToast.success(t('MyQuote.Requote_Request_Submitted'));
        } catch (error: any) {
            WalToast.error(error?.response?.data?.message || t('MyQuote.Requote_Request_Failed'));
        }
    };

    const handleSaveWidgets = useCallback(
        async (updatedWidgets: IWidget[]) => {
            const widgetsObject = transformWidgetsToApiFormat(updatedWidgets);

            const payload = {
                type: WIDGET_SECTION_TYPE.QUOTE,
                settings: widgetsObject,
            };

            const response = await updateUserWidgets(payload);

            scrollWidgetsToTop();

            dispatch(
                setCurrentUser({
                    ...currentUser,
                    quoteWidgetSettings: response?.data?.quoteWidgetSettings ?? widgetsObject,
                })
            );
        },
        [dispatch, currentUser, scrollWidgetsToTop]
    );

    // Prepare map data for MapItWidget
    const mapData = useMemo(() => {
        const shipperLat = quotingDetails?.shipperLatitude;
        const shipperLng = quotingDetails?.shipperLongitude;
        const consigneeLat = quotingDetails?.consigneeLatitude;
        const consigneeLng = quotingDetails?.consigneeLongitude;

        const formattedStartPointName =
            quotingDetails?.shipperCity && quotingDetails?.shipperStateCode ? `${quotingDetails?.shipperCity}, ${quotingDetails?.shipperStateCode}` : quotingDetails?.shipperFullAddress || null;

        const formattedEndPointName =
            quotingDetails?.consigneeCity && quotingDetails?.consigneeStateCode
                ? `${quotingDetails?.consigneeCity}, ${quotingDetails?.consigneeStateCode}`
                : quotingDetails?.consigneeFullAddress || null;

        const startPoint =
            shipperLat && shipperLng
                ? {
                      lat: typeof shipperLat === 'string' ? parseFloat(shipperLat) : shipperLat,
                      lng: typeof shipperLng === 'string' ? parseFloat(shipperLng) : shipperLng,
                      name: formattedStartPointName || null,
                  }
                : null;

        const endPoint =
            consigneeLat && consigneeLng
                ? {
                      lat: typeof consigneeLat === 'string' ? parseFloat(consigneeLat) : consigneeLat,
                      lng: typeof consigneeLng === 'string' ? parseFloat(consigneeLng) : consigneeLng,
                      name: formattedEndPointName || null,
                  }
                : null;

        const preventCrossBorder = !!(quotingDetails?.shipperCountry && quotingDetails?.consigneeCountry && quotingDetails?.shipperCountry === quotingDetails?.consigneeCountry);

        return {
            startPoint,
            endPoint,
            waypoints: [],
            currentLocation: null,
            preventCrossBorder,
            statusInfo: quotingDetails?.status
                ? {
                      status: getBadgeLabelByQuoteStatus(quotingDetails.status),
                      statusType: getBadgeTypeByQuoteStatus(quotingDetails.status),
                  }
                : undefined,
        };
    }, [quotingDetails]);

    // Task 25 (client decision) — stops data for the new stop-wise Map-It widget
    // (MultiStopMapItWidget). Multi-stop quotes already carry the full stop list (every position,
    // 1..N, with lat/lng) via getQuoteById's additive `stops` field (see
    // wal-customer-portal-be/getQuoteById.controller.ts). Classic 2-stop quotes never populate
    // that field, so a 2-item stops array is synthesized here from the same shipper/consignee
    // points mapData already computed above — lets MultiStopMapItWidget be a uniform drop-in
    // replacement for the old single-route MapItWidget mount regardless of quote type.
    // The route ALWAYS runs shipper -> consignee, with every other stop drawn as an in-between dot —
    // the same shape the internal WAL app builds (startPoint = shipper, endPoint = consignee,
    // waypoints in between; see getOrderMapData in wal-frontend-v2). A true multi-stop quote's
    // `stops` already carries the shipper/consignee bookends, but other quote types (e.g. multi_leg)
    // return only the in-between rows — taking that list as-is dropped the real lane and pinned the
    // map on a lone middle stop (user-reported: Montreal -> Baltimore quote showing only Queens, NY).
    // So the bookends are matched by coordinates and re-added whenever the API list lacks them.
    const stopsForMap = useMemo(() => {
        const toCoord = (value: any) => {
            const parsed = typeof value === 'string' ? parseFloat(value) : value;

            return parsed === null || parsed === undefined || Number.isNaN(parsed) ? null : parsed;
        };

        // ~55m tolerance: a bookend stop is built from the same shipper/consignee record, so the
        // coordinates match exactly — the tolerance only guards against float/precision drift.
        const isSamePlace = (aLat: number | null, aLng: number | null, bLat?: number, bLng?: number) =>
            aLat !== null && aLng !== null && bLat !== undefined && bLng !== undefined && Math.abs(aLat - bLat) < 0.0005 && Math.abs(aLng - bLng) < 0.0005;

        const shipperStop = mapData.startPoint
            ? {
                  city: quotingDetails?.shipperCity ?? null,
                  state: quotingDetails?.shipperState ?? quotingDetails?.shipperStateCode ?? null,
                  lat: mapData.startPoint.lat,
                  lng: mapData.startPoint.lng,
                  fullAddress: quotingDetails?.shipperFullAddress,
                  latitude: quotingDetails?.shipperLatitude,
                  longitude: quotingDetails?.shipperLongitude,
                  country: quotingDetails?.shipperCountry,
              }
            : null;

        const consigneeStop = mapData.endPoint
            ? {
                  city: quotingDetails?.consigneeCity ?? null,
                  state: quotingDetails?.consigneeState ?? quotingDetails?.consigneeStateCode ?? null,
                  lat: mapData.endPoint.lat,
                  lng: mapData.endPoint.lng,
                  fullAddress: quotingDetails?.consigneeFullAddress,
                  latitude: quotingDetails?.consigneeLatitude,
                  longitude: quotingDetails?.consigneeLongitude,
                  country: quotingDetails?.consigneeCountry,
              }
            : null;

        // Task 28 (client decision) — unitsOn/unitsOff (what boards/departs at each stop) for the
        // Master Route hover tooltip, sourced from the same multiStopSummary the Route strip above
        // already displays (deriveMultiStopSummary, wal-customer-portal-be). Resolved here against
        // the stop's ORIGINAL position, before the combined route is renumbered below.
        const summaryByPosition = new Map((quotingDetails?.multiStopSummary?.stops || []).map((s: any) => [s.position, s]));

        const apiStops = [...(quotingDetails?.stops || [])]
            .sort((a: any, b: any) => a.position - b.position)
            .map((stop: any) => ({
                city: stop.city ?? null,
                // Task 29 — full state name preferred (matches MultiStopRouteStrip's "Houston,
                // Texas" style — deriveMultiStopSummary on the BE prefers `state` the same way),
                // falling back to the abbreviated stateCode only if the full name is missing.
                state: stop.state ?? stop.stateCode ?? null,
                lat: toCoord(stop.latitude),
                lng: toCoord(stop.longitude),
                unitsOn: (summaryByPosition.get(stop.position) as any)?.unitsOn,
                unitsOff: (summaryByPosition.get(stop.position) as any)?.unitsOff,
                fullAddress: stop.fullAddress,
                latitude: stop.latitude,
                longitude: stop.longitude,
                country: stop.country,
            }));

        // Without shipper/consignee coordinates there is no lane to anchor to, so the API list is
        // all there is to show (classic quotes with no stops simply end up with an empty route).
        const combined =
            shipperStop && consigneeStop
                ? [
                      shipperStop,
                      ...apiStops.filter((stop) => !isSamePlace(stop.lat, stop.lng, shipperStop.lat, shipperStop.lng) && !isSamePlace(stop.lat, stop.lng, consigneeStop.lat, consigneeStop.lng)),
                      consigneeStop,
                  ]
                : apiStops;

        // One sequential numbering for the whole route so the map widget, the "Open" lane overview
        // and getStopAddressByPosition below all speak about the same stop.
        return combined.map((stop, index) => ({ ...stop, position: index + 1 }));
    }, [quotingDetails, mapData.startPoint, mapData.endPoint]);

    // Cards for the read-only Stops Summary modal (see isShowStopDetailsModal above) — same builder
    // CustomerPortalStopMetaStrip uses for its own strip, so the strip and the modal always show
    // identical stop info. Only meaningful once a genuine middle stop exists (length > 2 — the same
    // bookends-only guard used everywhere else on this page).
    const stopDetailCards = useMemo(
        () =>
            (quotingDetails?.stops?.length ?? 0) > 2
                ? buildStopCards({
                      stops: quotingDetails.stops,
                      summary: quotingDetails.multiStopSummary,
                      selectedServices: quotingDetails.selectedServices,
                      dimensions: quotingDetails.dimensions,
                      services,
                      t,
                  })
                : [],
        [quotingDetails?.stops, quotingDetails?.multiStopSummary, quotingDetails?.selectedServices, quotingDetails?.dimensions, services, t]
    );

    // Resolves a stop position (1..N) to the full address data Lane Overview needs — used by the
    // Map-It "Open" handler below to show whichever leg/Master Route is currently selected, instead
    // of always the overall shipper→consignee route. Mirrors stopsForMap's own position-1/position-2
    // fallback for classic (non-multi-stop) quotes, which never populate quotingDetails.stops.
    const getStopAddressByPosition = (position: number) => {
        // stopsForMap is the same shipper -> stops -> consignee route the widget renders, so the
        // positions it reports back on "Open" resolve here without re-deriving the route.
        if (stopsForMap.length > 0) {
            const stop = stopsForMap.find((s: any) => s.position === position);

            return {
                fullAddress: stop?.fullAddress,
                latitude: stop?.latitude,
                longitude: stop?.longitude,
                country: stop?.country,
            };
        }

        if (position === 1) {
            return {
                fullAddress: quotingDetails?.shipperFullAddress,
                latitude: quotingDetails?.shipperLatitude,
                longitude: quotingDetails?.shipperLongitude,
                country: quotingDetails?.shipperCountry,
            };
        }

        return {
            fullAddress: quotingDetails?.consigneeFullAddress,
            latitude: quotingDetails?.consigneeLatitude,
            longitude: quotingDetails?.consigneeLongitude,
            country: quotingDetails?.consigneeCountry,
        };
    };

    // Client decision — Lane Overview's map must mirror the Map-It widget's single joined route:
    // every stop strictly between fromPosition/toPosition shown as a waypoint dot, not just a
    // direct from->to line. Classic (non-multi-stop) quotes never populate quotingDetails.stops, so
    // this naturally resolves to [] for them.
    const getStopsAddressesBetween = (fromPosition: number, toPosition: number) =>
        stopsForMap
            .filter((stop: any) => stop.position > fromPosition && stop.position < toPosition)
            .map((stop: any) => ({
                fullAddress: stop?.fullAddress,
                latitude: stop?.latitude,
                longitude: stop?.longitude,
                country: stop?.country,
            }));

    // Real driving distance (Mapbox Directions API — same call the Map-It
    // widget below makes) for the "Preparing your quote" lane insights. A
    // straight-line (haversine) estimate was tried first and rejected: it
    // undercounts routes that can't go direct, e.g. Richmond, BC -> Toronto,
    // ON reads ≈2,090 mi haversine vs ≈2,726 mi actually driven — visibly
    // wrong next to the Map-It widget showing the real number on the same page.
    const [drivingMiles, setDrivingMiles] = useState<number | null>(null);

    useEffect(() => {
        const { shipperLatitude, shipperLongitude, consigneeLatitude, consigneeLongitude } = quotingDetails || {};

        if (!shipperLatitude || !shipperLongitude || !consigneeLatitude || !consigneeLongitude) {
            setDrivingMiles(null);

            return undefined;
        }

        let isCancelled = false;
        mapboxgl.accessToken = window.MAPBOX_ACCESSTOKEN;

        getRouteStats(
            { lat: parseFloat(shipperLatitude), lng: parseFloat(shipperLongitude) },
            { lat: parseFloat(consigneeLatitude), lng: parseFloat(consigneeLongitude) },
            t,
            mapData.preventCrossBorder
        ).then((stats) => {
            if (!isCancelled && stats.distance > 0) {
                setDrivingMiles(Math.round(stats.distance * 0.000621371));
            }
        });

        return () => {
            isCancelled = true;
        };
    }, [quotingDetails?.shipperLatitude, quotingDetails?.shipperLongitude, quotingDetails?.consigneeLatitude, quotingDetails?.consigneeLongitude, mapData.preventCrossBorder, t]);

    // Shipment/lane context for the "Preparing your quote" loader shown while
    // active carrier quotes are being fetched (see SelectQuoteBlock / QuotePreparingLoader).
    const quotePreparingLaneContext = useMemo<IQuotePreparingLaneContext>(() => {
        const totalUnits = quotingDetails?.dimensions?.reduce((sum: number, dimension: any) => sum + (dimension?.handlingUnitNo || 0), 0) || undefined;
        const weightMeasurement = quotingDetails?.dimensions?.[0]?.weightMeasurement;
        const handlingUnitId = quotingDetails?.dimensions?.[0]?.handlingUnit;
        const handlingUnitName = handlingUnitId ? handlingUnitOptions?.find((option: any) => String(option.value) === String(handlingUnitId))?.label?.toLowerCase() : undefined;

        return {
            classification: quotingDetails?.classification,
            // Server-derived: standard LTL (rates automatically) vs Volume LTL / FTL / Specialized
            // (a sales rep prices it). Picks which wording the loader's timeline uses — the
            // threshold rule lives in the BE so it stays tied to WAL's Quote Settings.
            isInstantLtl: quotingDetails?.isInstantLtl,
            shipperCity: quotingDetails?.shipperCity,
            shipperStateCode: quotingDetails?.shipperStateCode,
            shipperCountryCode: quotingDetails?.shipperCountryCode,
            shipperLatitude: quotingDetails?.shipperLatitude,
            shipperLongitude: quotingDetails?.shipperLongitude,
            consigneeCity: quotingDetails?.consigneeCity,
            consigneeStateCode: quotingDetails?.consigneeStateCode,
            consigneeCountryCode: quotingDetails?.consigneeCountryCode,
            consigneeLatitude: quotingDetails?.consigneeLatitude,
            consigneeLongitude: quotingDetails?.consigneeLongitude,
            totalUnits,
            handlingUnitName,
            totalWeight: quotingDetails?.totalWeight,
            weightUnitLabel: weightMeasurement ? getLabelByValue(weightMeasurement)?.toLowerCase() : undefined,
            pickupDate: quotingDetails?.pickupDate,
            // "DNS" (Do Not Stack) toggle on a dimension row — isStack:true means
            // that pallet may NOT be stacked, i.e. non-stackable (confusingly named
            // to match the field the dimensions form already persists).
            hasNonStackablePallets: Boolean(quotingDetails?.dimensions?.some((dimension: any) => dimension?.isStack === true)),
            drivingMiles,
        };
    }, [quotingDetails, handlingUnitOptions, drivingMiles]);

    const isCodeLoading = !code && !quotingDetails?.code && isLoading;

    const stopsTotal = quotingDetails?.stops?.length ?? Number(quotingDetails?.stopsCount ?? 0);
    const isStripLoading = isLoading && !quotingDetails?.stops?.length;

    return (
        <PageSectionLayout
            headerClassName="stepper-container w-full"
            contentClassName="stepper-container overflow-hidden !p-0 !gap-0"
            header={
                <Header
                    title={
                        <div className="flex items-center md:gap-2 gap-1">
                            <ButtonCmp
                                className="btn_secondary_black"
                                size="square-sm"
                                onClick={() => {
                                    navigate(PATH.MY_QUOTE, { replace: true });
                                }}
                            >
                                <ArrowLeft className="w-4 h-4" />
                            </ButtonCmp>
                            <span className={isCodeLoading ? 'custom-loading' : ''}>{code ? `#${code}` : quotingDetails?.code ? `#${quotingDetails.code}` : isLoading ? 'SQ-NK-200352' : ''}</span>
                        </div>
                    }
                    leftContent={
                        <>
                            {/* Skeleton while the code is still loading — otherwise this icon rendered solid
                                between the skeleton title and badge, and copied an empty string when clicked */}
                            {isCodeLoading ? (
                                <div className="w-5 h-5 rounded custom-loading" />
                            ) : (
                                <TooltipCmp message={t('Common.Copy')}>
                                    <Copy05
                                        className={`w-5 h-5 text-primary cursor-pointer font-bold`}
                                        onClick={() => {
                                            copyDebounce(code || quotingDetails?.code || '');
                                        }}
                                    />
                                </TooltipCmp>
                            )}

                            {isLoadingActiveQuotes || !quotingDetails?.code ? (
                                <div className="flex h-6 w-20 border justify-center items-center border-gray300 rounded-lg gap-1 custom-loading">
                                    <span className="rounded-full p-1 bg-warning500"></span>
                                    <span className="text-xs">{t('QuotingHub.QuotingRequest.To_Quote')} </span>
                                </div>
                            ) : (
                                quotingDetails?.status && (
                                    <BadgeCmp
                                        style="modern"
                                        mainClassName="bg-white"
                                        type={getBadgeTypeByQuoteStatus(activeQuotes.length > 0 && quotingDetails?.status !== STATUS.BOOKED ? STATUS.ACTIVE : quotingDetails?.status)}
                                    >
                                        {getBadgeLabelByQuoteStatus(activeQuotes.length > 0 && quotingDetails?.status !== STATUS.BOOKED ? STATUS.ACTIVE : quotingDetails?.status)}
                                    </BadgeCmp>
                                )
                            )}
                        </>
                    }
                    mainContainerClassName="xls:!flex-nowrap !flex-wrap mdm:!flex-row md:!flex-col sm:!flex-row flex-col mdm:!items-center !items-start"
                    rightClassName="mdm:min-w-fit md:min-w-full sm:min-w-fit min-w-full !w-fit"
                    rightSideContent={
                        <>
                            {/* Client decision — Order ID moved here from the Select-a-Quote table's
                                trailing column (which displayed quotingDetails.rossRocketPublicId —
                                a RoseRocket booking reference set as soon as the quote moves to
                                TO_BOOK, well before wal-backend-v2 finishes actually converting it
                                into an internal Order row). Label always uses rossRocketPublicId so
                                it shows as soon as the old column did; the button only becomes a
                                real link once quotingDetails.linkedOrder resolves (additive field
                                from fetchQuoteDetailById, populated from quote.e2eOrderId) — until
                                then it's disabled with an "in progress" tooltip. No button/tooltip
                                at all when rossRocketPublicId itself isn't set yet. */}
                            {isToBookStatus && quotingDetails?.rossRocketPublicId && (
                                <TooltipCmp message={!quotingDetails?.linkedOrder ? t('QuotingHub.Order_In_Progress_Tooltip') : ''}>
                                    <ButtonCmp
                                        // Client decision — once the order actually exists (a real link, not just
                                        // pending), the button matches Quote Actions' style exactly.
                                        className={`${quotingDetails?.linkedOrder ? 'btn-outline-primary' : 'btn_secondary_black'} ${isLoading ? 'custom-loading' : ''}`}
                                        disabled={isLoading || !quotingDetails?.linkedOrder}
                                        onClick={() => {
                                            if (quotingDetails?.linkedOrder) {
                                                navigate(`/orders/${quotingDetails.linkedOrder.id}`);
                                            }
                                        }}
                                    >
                                        {`#${quotingDetails.rossRocketPublicId}`}
                                    </ButtonCmp>
                                </TooltipCmp>
                            )}
                            <OutsideClickHandler onOutsideClick={() => setIsShowQuoteActionsDropDown(false)} containerClassName="relative">
                                <ButtonCmp
                                    className={`btn-outline-primary ${isLoading ? 'custom-loading' : ''}`}
                                    onClick={() => !isLoading && setIsShowQuoteActionsDropDown((prev) => !prev)}
                                    disabled={isLoading}
                                >
                                    {t('QuotingHub.Quote_Actions')}
                                    <ChevronSelectorVertical className="w-3.5 h-3.5 transition-transform duration-200" />
                                </ButtonCmp>
                                <ul
                                    className={`shadow text-xs rounded-lg break-words cursor-pointer empty:hidden bg-white border border-utilityGray200 p-1 my-1.5 z-[1] min-w-[150px] w-fit right-0 absolute leading-[18px] ${
                                        isShowQuoteActionsDropDown ? '' : 'hidden'
                                    }`}
                                >
                                    {/* Client decision — customers can't edit a quote after creation; "Duplicate Quote"
                                        (same flow as the list row's copy icon) replaces "Edit Quote" here. */}
                                    <li
                                        className={`text-xs leading-[1.5] font-normal text-grayLight900 rounded-md bg-transparent hover:bg-white800 p-1 transition-all duration-[0.3s] cursor-pointer flex items-center gap-x-2 ${
                                            duplicatingQuoteId ? 'pointer-events-none opacity-50' : ''
                                        }`}
                                        onClick={() => {
                                            setIsShowQuoteActionsDropDown(false);
                                            duplicateQuote(quotingDetails);
                                        }}
                                    >
                                        <Copy04 className="w-3.5 h-3.5 text-grayLight900 flex-none" />
                                        <p className="flex-1">{t('QuotingHub.Duplicate_Quote')}</p>
                                    </li>
                                </ul>
                            </OutsideClickHandler>
                        </>
                    }
                />
            }
        >
            <div className="flex h-full flex-col w-full min-h-0">
                <div className="px-[var(--layout-page-content-left-spacing)] pt-[var(--layout-page-content-top-spacing)]">
                    <QuoteDetailMetaDataTable
                        dataList={quotingDetails?.id ? [quotingDetails] : []}
                        // No onOpenModal — the metadata card must not open the Edit Quote modal anymore.
                        // The "Quote Type" cell is the one exception: it opens the read-only Stops
                        // Summary info modal below, which edits nothing.
                        isLoading={isLoading}
                        onPickupDateWarningClick={handlePickupDateWarningClick}
                        onOpenStopDetails={stopDetailCards.length > 0 ? () => setIsShowStopDetailsModal(true) : undefined}
                    />
                </div>
                <div className="flex overflow-auto custom-scrollbar-main flex-1">
                    <div className="flex-1 flex flex-col overflow-hidden pl-[var(--layout-page-content-left-spacing)] pr-2 pt-5">
                        <div
                            ref={scrollContainerRef}
                            className={`flex-1 flex flex-col -mx-2 px-2 ${activeTab === 'messages' ? 'overflow-hidden pb-4' : 'overflow-auto custom-scrollbar-v2 pb-4'} space-y-5`}
                        >
                            <div className="sticky top-0 z-[10] global-background-color">
                                <TabButton
                                    parentClassName="tab-border-bottom-parent border-b !border-borderPrimary w-full"
                                    activeClassName="tab-border-bottom-active"
                                    tabParentClassName="px-1 -mx-1 global-background-color"
                                    className="tab-border-bottom flex-1 max-w-[200px] !pt-[6px]"
                                    tabBadgeClassName="tab-border-bottom-badge"
                                    tabArray={quoteDetailTabs}
                                    isActive={activeTab}
                                    handleOnClick={(e: any) => setActiveTab(e.currentTarget.dataset.value)}
                                />
                            </div>
                            {activeTab === 'overview' && (
                                <>
                                    {/* Bug fix (user-reported) — quotingDetails.stops always includes the shipper/consignee
                                        bookends even when there are no real (customer-requested) middle stops left after
                                        the backend's filterCustomerVisibleStops (e.g. a quote created from the WAL side).
                                        length > 2 means at least one genuine middle stop survived — a plain 2-point route
                                        should render exactly like a classic quote, with no stop-summary strip at all. */}
                                    {stopsTotal > 2 && (
                                        <CustomerPortalStopMetaStrip
                                            stops={quotingDetails.stops ?? []}
                                            summary={quotingDetails.multiStopSummary}
                                            dimensions={quotingDetails.dimensions}
                                            selectedServices={quotingDetails.selectedServices}
                                            isLoading={isStripLoading}
                                            skeletonStopCount={stopsTotal}
                                        />
                                    )}
                                    <SelectQuoteBlock
                                        onBookOrder={handleBookOrder}
                                        isLoading={isLoadingActiveQuotes || isLoading}
                                        isToBookStatus={isToBookStatus}
                                        pickupDate={quotingDetails?.pickupDate}
                                        onDuplicate={handleDuplicate}
                                        onRequestRate={handleRequestRate}
                                        laneContext={quotePreparingLaneContext}
                                        isNewQuote={isNewQuote}
                                    />
                                    <ActivityFeedCmp
                                        entityType="quote"
                                        entityId={Number(currentQuoteId)}
                                        handleLoadMore={handleLoadMore}
                                        notePlaceholder="Add an activity or note for this quote"
                                        wrapperClassName="min-h-[360px]"
                                        isActivityBox="sm"
                                    />
                                </>
                            )}
                            {/* Not mounted for a WAL staff session — mounting it fires chat fetches the
                                backend answers with 403 "Chat is not available for WAL staff sessions." */}
                            {!currentUser?.isWalUserSession && (
                                <div className={`flex flex-1 min-h-0 h-full ${activeTab !== 'messages' ? 'hidden' : ''}`}>
                                    <EntityChatView
                                        entityId={currentQuoteId || ''}
                                        entityType="quote"
                                        isActive={activeTab === 'messages'}
                                        mainLoading={isLoading}
                                        // quote.salesRep = the assigned sales rep (fetchQuoteDetailById) — header falls back to currentUser.salesRep
                                        entitySalesRep={toEntityChatSalesRep(quotingDetails?.salesRep)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div ref={rightScrollContainerRef} className="widget-right-sidebar-common-wrap flex flex-col overflow-auto custom-scrollbar-v2 space-y-5 pt-5">
                        <div className="widget-right-sidebar-common-content-x-spacing">
                            <div className="border-b border-borderPrimary w-full pb-1.5 flex items-center justify-between">
                                <h6 className="text-textSecondary text-sm font-semibold">Widgets</h6>
                                <TooltipCmp message="Customize Widgets">
                                    <ButtonCmp className="btn_secondary_black" size="square-sm" onClick={() => setIsWidgetsModalOpen(true)}>
                                        <Settings04 className="w-4 h-4" />
                                    </ButtonCmp>
                                </TooltipCmp>
                            </div>
                        </div>

                        {isWidgetsDisabled ? (
                            <div className="widget-right-sidebar-common-content-x-spacing">
                                <div className="table-box-wrap min-h-[192px] flex items-center justify-center">
                                    <NotFoundUI
                                        title="No Widgets Added"
                                        desc="Enable widgets from the settings to see them here."
                                        iconClassName="!hidden"
                                        iconSecondaryClassName="!hidden"
                                        descClass="!px-0"
                                        containerClassName="!my-[0px] !h-auto"
                                        contentClassName="before:!bg-none"
                                        iconSecondary={<Grid01 className="w-5 h-5 text-primary" />}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div ref={widgetsContentRef} className="widget-right-sidebar-common-content widget-right-sidebar-common-content-x-spacing !mt-0">
                                {/* Keyed by widget.id, never the array index — the widget list is rebuilt when
                                    currentUser.quoteWidgetSettings loads, and an index key would make React
                                    reuse the slot for a different widget, remounting MessagesWidget and
                                    re-firing its chat fetches. */}
                                {widgets.map((widget) => (
                                    <React.Fragment key={widget.id}>
                                        {widget.id === QUOTE_WIDGET_IDS.MAP_IT && widget.isEnabled && (
                                            <MultiStopMapItWidget
                                                stops={stopsForMap}
                                                title={t('QuotingHub.PredictivePricing.Map-It')}
                                                preventCrossBorder={mapData.preventCrossBorder}
                                                onOpenClick={({ fromPosition, toPosition }) => {
                                                    setLaneOverviewRoute({
                                                        from: getStopAddressByPosition(fromPosition),
                                                        to: getStopAddressByPosition(toPosition),
                                                        waypoints: getStopsAddressesBetween(fromPosition, toPosition),
                                                    });
                                                    setIsShowLaneOverviewModal(true);
                                                }}
                                                statusInfo={mapData.statusInfo as any}
                                                openButtonLabel={t('QuotingHub.QuotingDashboard.Open')}
                                                isLoading={isLoading}
                                            />
                                        )}
                                        {widget.id === QUOTE_WIDGET_IDS.TERMS_AND_CONDITION && widget.isEnabled && <TermsAndConditions />}
                                        {/* Chat is unavailable for WAL staff sessions (no contact participant row) */}
                                        {widget.id === QUOTE_WIDGET_IDS.MESSAGES && widget.isEnabled && !currentUser?.isWalUserSession && (
                                            <MessagesWidget quoteId={currentQuoteId || ''} mainLoading={isLoading} />
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {isShowQuoteDetailModal && (
                <QuoteDetailModalNew
                    handleUpdate={(updatedData: any) => {
                        // updatedData = { quote, dimensions, services, hasQuoteDataChanged, stops }
                        // (interceptor unwraps response.data, then handleUpdate receives result.data)
                        if (updatedData?.quote) {
                            // `stops` must be included here — quotingDetails.stops feeds the Edit
                            // modal's middle-stop tabs on reopen, so omitting it made stop edits
                            // appear to silently revert even though the save itself succeeded.
                            setQuotingDetails((prev: any) => ({
                                ...prev,
                                ...updatedData.quote,
                                dimensions: updatedData.dimensions,
                                selectedServices: updatedData.services,
                                stops: updatedData.stops,
                            }));
                        }
                    }}
                    setChangedByCustomer={(value: boolean) => {
                        setIsChangedByCustomer(value);

                        if (value) {
                            setIsLoadingActiveQuotes(true);
                        }
                    }}
                />
            )}
            {isShowLaneOverviewModal && (
                <LaneOverviewModel
                    isOpen={isShowLaneOverviewModal}
                    handleClose={() => setIsShowLaneOverviewModal(false)}
                    fromFullAddress={
                        laneOverviewRoute?.from ?? {
                            fullAddress: quotingDetails?.shipperFullAddress,
                            latitude: quotingDetails?.shipperLatitude,
                            longitude: quotingDetails?.shipperLongitude,
                            country: quotingDetails?.shipperCountry,
                        }
                    }
                    toFullAddress={
                        laneOverviewRoute?.to ?? {
                            fullAddress: quotingDetails?.consigneeFullAddress,
                            latitude: quotingDetails?.consigneeLatitude,
                            longitude: quotingDetails?.consigneeLongitude,
                            country: quotingDetails?.consigneeCountry,
                        }
                    }
                    waypoints={laneOverviewRoute?.waypoints ?? []}
                />
            )}
            {isShowDatePickupModal && <DatePickupModel handleClose={handleDatePickupModalClose} onUpdate={handlePickupDateUpdate} setChangedByCustomer={setIsChangedByCustomer} />}
            {isShowStopDetailsModal && (
                <StopDetailsModal cards={stopDetailCards} code={code || quotingDetails?.code} handleClose={() => setIsShowStopDetailsModal(false)} handlingUnitOptions={handlingUnitOptions} />
            )}
            {isWidgetsModalOpen && <WidgetSettingsModal handleClose={() => setIsWidgetsModalOpen(false)} widgets={widgets} onSave={handleSaveWidgets} />}

            {pendingBookingQuoteData && (
                <BookOrderBillingAccountModal
                    divisions={bookableDivisions}
                    fixedDivisionId={bookingBillingSelection.division?.id ?? null}
                    showDivision={bookingBillingSelection.needsDivision}
                    showBillingAccount={bookingBillingSelection.needsBillingAccount}
                    handleClose={() => setPendingBookingQuoteData(null)}
                    onConfirm={({ division, billingAccount }) => {
                        // Read the parked row before clearing it — proceedToBooking navigates away.
                        const quoteData = pendingBookingQuoteData;
                        setPendingBookingQuoteData(null);
                        proceedToBooking(quoteData, billingAccount ?? bookingBillingSelection.billingAccount, division);
                    }}
                />
            )}
        </PageSectionLayout>
    );
};
export default QuoteDetail;
